import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useConfigStore } from '@dashboard/stores/configStore'
import { makeId, idPrefixes } from '@dashboard/services/uuid'
import type { Phrase } from '@dashboard/types/config'
import { storeToRefs } from 'pinia'

// Untouched segment inputs leave undefined overrides; renderers use the fallback text.
type PhraseInput = Partial<Omit<Phrase, 'map'>> & { map?: Record<string, string | undefined> }

export const usePhraseStore = defineStore('phrase', () => {
  const configStore = useConfigStore()
  const { config } = storeToRefs(configStore)

  const phrases = computed(() => config.value?.phrases || [])

  const getById = (id: string) => phrases.value.find((phrase) => phrase.id === id)

  const upsert = (phrase: PhraseInput) => {
    const existingPhrase = getById(phrase.id || '')
    if (existingPhrase) {
      Object.assign(existingPhrase, {
        fallbackText: phrase.fallbackText,
        map: phrase.map,
        segmentGroupId: phrase.segmentGroupId
      })
      return existingPhrase
    }
    const newPhrase = {
      id: makeId(idPrefixes.PHRASE),
      ...phrase
    } as Phrase

    config.value?.phrases?.push(newPhrase)
    return newPhrase
  }

  const destroy = (phrase: Phrase) => {
    const foundIndex = config.value?.phrases?.findIndex((p) => p.id === phrase.id)
    if (foundIndex === undefined || foundIndex === -1) {
      return
    } // this should really never happen

    config.value?.phrases?.splice(foundIndex, 1)
  }

  return {
    phrases,
    getById,
    upsert,
    destroy
  }
})
